import { Bot } from "lucide-react";
import { type Transition, motion, useReducedMotion } from "motion/react";
import { type MouseEvent, forwardRef, useId } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, type To } from "react-router-dom";

import {
  SYSTEM_ACTOR_NAME,
  resolveActorName,
  translateActorName,
} from "./utils";
import { NOTIFICATION_ACTIONS, type NotificationAction } from "../constants";
import { getNotificationIcon } from "../notification-icon";
import type { NotificationActionOptions, NotificationData } from "../types";
import {
  getNotificationSeverityStyles,
  getTranslationKey,
  getTranslationValues,
} from "../utils";

import { Avatar, AvatarFallback } from "@/components/ui/v2/avatar";
import { Card } from "@/components/ui/v2/card";
import { Checkbox } from "@/components/ui/v2/checkbox";
import { RelativeTime } from "@/components/ui/v2/relative-time";
import { cn } from "@/lib/utils/cn";
import { getInitials } from "@/utils/stringUtils";

const MIDDLE_MOUSE_BUTTON = 1;

const enterTransition: Transition = {
  type: "spring",
  bounce: 0,
  duration: 0.35,
};

const layoutTransition: Transition = {
  type: "spring",
  stiffness: 500,
  damping: 35,
  mass: 0.5,
};

const reducedMotionTransition: Transition = {
  duration: 0.15,
};

const exitTransition: Transition = {
  duration: 0.2,
  ease: [0.25, 1, 0.5, 1],
};

function getRowTransition(
  prefersReducedMotion: boolean | null,
  isNew: boolean | undefined,
) {
  if (prefersReducedMotion) return reducedMotionTransition;
  if (isNew) return enterTransition;
  return layoutTransition;
}

export type NotificationRowSelection = {
  isChecked: boolean;
  onToggle: (
    notificationId: NotificationData["id"],
    extendRange: boolean,
  ) => void;
};

export type NotificationRowProps = {
  notification: NotificationData;
  onOpen?: () => void;
  onAction: (
    notificationId: NotificationData["id"],
    action: NotificationAction,
    options?: NotificationActionOptions,
  ) => void;
  selection?: NotificationRowSelection;
  isNew?: boolean;
  isSelected?: boolean;
  href?: To;
};

export const NotificationRow = forwardRef<HTMLDivElement, NotificationRowProps>(
  function NotificationRow(
    { notification, onOpen, onAction, selection, isNew, isSelected, href },
    ref,
  ) {
    const { t } = useTranslation();
    const prefersReducedMotion = useReducedMotion();
    const messageId = useId();
    const isUnread = notification.readAt === null;
    const severityStyles = getNotificationSeverityStyles(notification.severity);
    const actorName = resolveActorName(notification.actorName);
    const actorDisplayName = translateActorName(actorName, t);
    const DomainIcon = getNotificationIcon(notification.eventType);

    const handleOpen = () => {
      onOpen?.();
      if (!isUnread) return;
      onAction(notification.id, NOTIFICATION_ACTIONS.READ, {
        keepInView: true,
      });
    };

    const handleAuxClick = (event: MouseEvent) => {
      if (event.button !== MIDDLE_MOUSE_BUTTON) return;
      handleOpen();
    };

    const handleToggle = (event: MouseEvent) => {
      if (!selection) return;
      event.preventDefault();
      selection.onToggle(notification.id, event.shiftKey);
    };

    const content = (
      <div className="flex gap-3">
        <div
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full",
            severityStyles.background,
            severityStyles.icon,
          )}
        >
          <DomainIcon className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p
            id={messageId}
            className={cn(
              "wrap-break-word text-sm leading-snug",
              isUnread && "font-medium",
              severityStyles.text,
            )}
          >
            {isUnread && (
              <span className="sr-only">
                {t("notifications.unreadPrefix")}{" "}
              </span>
            )}
            <Trans
              i18nKey={getTranslationKey(notification)}
              values={getTranslationValues(notification, t)}
              components={{
                strong: (
                  <strong
                    className={isUnread ? "font-semibold" : "font-medium"}
                  />
                ),
              }}
            />
          </p>

          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <Avatar className="size-5 shrink-0">
              <AvatarFallback className="text-[11px] font-medium">
                {actorName === SYSTEM_ACTOR_NAME ? (
                  <Bot className="size-3" />
                ) : (
                  getInitials(actorDisplayName)
                )}
              </AvatarFallback>
            </Avatar>
            <span>{t("notifications.by", { name: actorDisplayName })}</span>
            <span aria-hidden="true" className="text-border">
              |
            </span>
            <RelativeTime date={notification.createdAt} tooltipSide="bottom" />
          </div>
        </div>
      </div>
    );

    const contentClassName = cn(
      "block min-w-0 flex-1 py-3 pr-2",
      selection ? "pl-1" : "pl-4",
      href &&
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
    );

    const entering = prefersReducedMotion
      ? { opacity: 0 }
      : { opacity: 0, y: "-100%" };
    const visible = prefersReducedMotion
      ? { opacity: 1 }
      : { opacity: 1, x: 0, y: 0 };
    const hidden = prefersReducedMotion
      ? { opacity: 0, transition: reducedMotionTransition }
      : { opacity: 0, x: 24, transition: exitTransition };

    return (
      <motion.div
        ref={ref}
        data-notification-row={notification.id}
        layout={!prefersReducedMotion}
        transition={getRowTransition(prefersReducedMotion, isNew)}
        initial={isNew ? entering : false}
        animate={visible}
        exit={hidden}
        className="px-3 py-1.5"
      >
        <Card
          hover={Boolean(href)}
          className={cn(
            "flex-row items-start gap-0 py-0",
            isSelected && "selected-tint-primary",
          )}
        >
          {selection && (
            <label className="flex shrink-0 cursor-pointer items-center self-stretch pr-2 pl-4 pointer-coarse:pr-3">
              <Checkbox
                checked={selection.isChecked}
                onClick={handleToggle}
                aria-labelledby={messageId}
                className="pointer-coarse:size-5"
              />
            </label>
          )}
          {href ? (
            <Link
              to={href}
              onClick={handleOpen}
              onAuxClick={handleAuxClick}
              aria-current={isSelected || undefined}
              className={contentClassName}
            >
              {content}
            </Link>
          ) : (
            <div className={contentClassName}>{content}</div>
          )}
          <span
            aria-hidden="true"
            className={cn(
              "mr-4 size-2 shrink-0 self-center rounded-full",
              isUnread ? "bg-primary" : "bg-transparent",
            )}
          />
        </Card>
      </motion.div>
    );
  },
);
